import { useEffect, useState, useCallback } from "react";
import { getStoryTasks } from "./StoriesService";
import { createPlannerItem, updatePlannerItem, updatePlannerStatus, deletePlannerItem } from "./ProjectsService";
import { useToast } from "../ToastContext";
import { TaskRow, TaskFormModal } from "./TaskViews";
import CommentThread from "../shared/CommentThread";
import "./EpicsStories.css";

const STATUS_META = {
  PLANNING:  { label: "Planning",   cls: "es-status-planning"  },
  ACTIVE:    { label: "Active",     cls: "es-status-active"    },
  ON_HOLD:   { label: "On Hold",    cls: "es-status-onhold"    },
  COMPLETED: { label: "Completed",  cls: "es-status-completed" },
  ARCHIVED:  { label: "Archived",   cls: "es-status-archived"  },
};

const PRIORITY_META = {
  CRITICAL: { label: "Critical", color: "#ef4444" },
  HIGH:     { label: "High",     color: "#f97316" },
  MEDIUM:   { label: "Medium",   color: "#eab308" },
  LOW:      { label: "Low",      color: "#22c55e" },
  SOMEDAY:  { label: "Someday",  color: "#6b7280" },
};

export default function StoryDetail({ projectName, projectColor, epicName, story, onBack, onEdit, onDelete }) {
  const toast = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [commentCount, setCommentCount] = useState(story.commentCount || 0);

  const load = useCallback(() => {
    setLoading(true);
    getStoryTasks(story.id)
      .then(setTasks)
      .catch(() => toast("Failed to load tasks", "error"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [story.id]);

  useEffect(() => { load(); }, [load]);

  const handleStatusChange = async (taskId, status) => {
    try {
      const updated = await updatePlannerStatus(taskId, status);
      setTasks(ts => ts.map(t => t.id === updated.id ? updated : t));
    } catch {
      toast("Failed to update status", "error");
    }
  };

  const handleSaveTask = async (data) => {
    try {
      if (editingTask) {
        const updated = await updatePlannerItem(editingTask.id, { ...editingTask, ...data });
        setTasks(ts => ts.map(t => t.id === updated.id ? updated : t));
        toast("Task updated", "success");
      } else {
        const created = await createPlannerItem({ ...data, projectName, type: "TODO" });
        setTasks(ts => [...ts, created]);
        toast("Task added", "success");
      }
      setShowForm(false);
      setEditingTask(null);
    } catch {
      toast(editingTask ? "Failed to update task" : "Failed to add task", "error");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await deletePlannerItem(taskId);
      setTasks(ts => ts.filter(t => t.id !== taskId));
      toast("Task deleted", "success");
    } catch {
      toast("Failed to delete task", "error");
    }
  };

  return (
    <div className="es-story-detail">
      <div className="es-breadcrumb">
        <button className="es-crumb-link" onClick={onBack}>🧩 {epicName}</button>
        <span className="es-crumb-sep">/</span>
        <span className="es-crumb-current">📖 {story.name}</span>
      </div>

      <div className="es-detail-header">
        <div>
          <h3>{story.name}</h3>
          {story.description && <p className="es-detail-desc">{story.description}</p>}
          <div className="es-detail-meta">
            <span className={`es-status-badge ${STATUS_META[story.status]?.cls || ""}`}>
              {STATUS_META[story.status]?.label || story.status}
            </span>
            {story.priority && (
              <span className="es-story-priority" style={{ color: PRIORITY_META[story.priority]?.color }}>
                {PRIORITY_META[story.priority]?.label}
              </span>
            )}
          </div>
        </div>
        <div className="es-detail-actions">
          <button className="proj-btn-secondary" onClick={onEdit}>✏️ Edit</button>
          <button className="proj-btn-secondary es-btn-danger" onClick={onDelete}>🗑 Delete</button>
        </div>
      </div>

      <div className="es-section-header">
        <h4>Tasks</h4>
        <button className="proj-btn-primary" onClick={() => { setEditingTask(null); setShowForm(true); }}>+ New Task</button>
      </div>

      {loading ? (
        <div className="es-empty">Loading tasks…</div>
      ) : tasks.length === 0 ? (
        <div className="es-empty">No tasks yet under this story.</div>
      ) : (
        <div className="proj-task-list">
          {tasks.map(t => (
            <TaskRow
              key={t.id}
              task={t}
              projectColor={projectColor}
              onStatusChange={(status) => handleStatusChange(t.id, status)}
              onEdit={() => { setEditingTask(t); setShowForm(true); }}
              onDelete={() => handleDeleteTask(t.id)}
            />
          ))}
        </div>
      )}

      <div className="es-section-header">
        <h4>Comments {commentCount > 0 && <span className="es-comment-count">({commentCount})</span>}</h4>
      </div>
      <CommentThread entityType="STORY" entityId={story.id} onCountChange={setCommentCount} />

      {showForm && (
        <TaskFormModal
          projectName={`${epicName} — ${story.name}`}
          task={editingTask}
          storyId={story.id}
          onSave={handleSaveTask}
          onClose={() => { setShowForm(false); setEditingTask(null); }}
        />
      )}
    </div>
  );
}
